import {Button, CrossIcon, DeleteIcon, EditIcon, MoreIcon, PopoverButton} from '@kit';

import React, {useCallback, useState} from 'react';

import type {User} from '../../generated/types';
import {Avatar} from '../avatar';
import {useDeleteConfirm} from '../use-delete-confirm';
import {cls} from '../utils';
import {ago} from '../utils/ago';
import {CommentForm} from './comment-form';
import {CommentNode, MAX_INDENT_DEPTH, flattenTree} from './comment-tree';
import {useCommentsContext} from './comments-context';

import popoverCss from '@kit/popover-button/popover-button.module.css';
import css from './comments.module.css';

const EDIT_WINDOW = 2 * 60 * 60 * 1000;

const dateFormat = new Intl.DateTimeFormat('ru-RU', {
	day: 'numeric',
	month: 'long',
	year: 'numeric',
	hour: '2-digit',
	minute: '2-digit',
	timeZone: 'Europe/Moscow',
});

type ListProps = {
	nodes: CommentNode[];
	depth: number;
};

export const CommentList: React.FC<ListProps> = ({nodes, depth}) => {
	const nested = depth < MAX_INDENT_DEPTH;
	const items = nested ? nodes : flattenTree(nodes);

	return (
		<ul className={cls(css.list, depth > 0 && css.children)}>
			{items.map((node) => (
				<CommentItem key={node.id} node={node} depth={depth} nested={nested} />
			))}
		</ul>
	);
};

type ItemProps = {
	node: CommentNode;
	depth: number;
	/** показывать ответы вложенным списком; на предельной глубине их выводит родительский список */
	nested: boolean;
};

const CommentItem: React.FC<ItemProps> = ({node, depth, nested}) => {
	const {canWrite, isAdmin, now, userId, blockedAuthors, reply, edit, remove, setBlocked} = useCommentsContext();
	const [mode, setMode] = useState<'view' | 'reply' | 'edit'>('view');

	const handleReply = useCallback(
		(text: string) => reply(node.id, text).then(() => setMode('view')),
		[reply, node.id],
	);
	const handleEdit = useCallback(
		(text: string) => edit(node.id, text).then(() => setMode('view')),
		[edit, node.id],
	);

	const {openConfirm, DeleteConfirm} = useDeleteConfirm({
		callback: () => remove(node.id),
		title: 'Удалить комментарий',
		text: 'Комментарий пропадёт со страницы. Ответы на него останутся.',
	});

	const authorBlocked = blockedAuthors.has(node.authorId);
	const canModerate = isAdmin && !node.isDeleted;
	const created = new Date(node.createdAt);
	const hasReplies = nested && node.children.length > 0;
	const canEditOwn = canWrite && node.authorId === userId && Date.now() - created.getTime() < EDIT_WINDOW;

	return (
		<li id={`comment-${node.id}`} className={css.item}>
			{DeleteConfirm}
			<div className={css.comment}>
				<div className={css.avatarColumn}>
					<Avatar user={{nickname: node.authorName} as User} size="size32" />
					{hasReplies && <div className={css.thread} />}
				</div>
				<div className={css.body}>
					<div className={css.head}>
						<span className={css.author}>{node.authorName}</span>
						<time className={css.date} dateTime={node.createdAt} title={dateFormat.format(created)}>
							{now ? ago(created, now) : dateFormat.format(created)}
						</time>
						{node.editedBy && !node.isDeleted && <span className={css.date}>· изменён {node.editedBy === 'admin' ? 'администратором' : 'автором'}</span>}
						{authorBlocked && <span className={css.blocked}>заблокирован</span>}
					</div>

					{mode === 'edit' ? (
						<CommentForm
							initialText={node.text}
							submitLabel="Сохранить"
							autoFocus
							onSubmit={handleEdit}
							onCancel={() => setMode('view')}
						/>
					) : node.isDeleted ? (
						<div className={cls(css.text, css.deleted)}>Комментарий удалён</div>
					) : (
						<div className={css.text}>{node.text}</div>
					)}

					{mode === 'view' && !node.isDeleted && (canWrite || canModerate) && (
						<div className={css.actions}>
							{canWrite && (
								<Button appearance="subtle" size="small" onClick={() => setMode('reply')}>
									Ответить
								</Button>
							)}
							{canEditOwn && !canModerate && (
								<Button appearance="subtle" size="small" onClick={() => setMode('edit')}>
									Изменить
								</Button>
							)}
							{canModerate && (
								<PopoverButton
									appearance="subtle"
									size="small"
									align="end"
									icon={<MoreIcon width={20} height={20} />}
									aria-label="Действия администратора"
									popover={
										<div className={popoverCss.popoverButtons}>
											<Button appearance="subtle" icon={<EditIcon />} onClick={() => setMode('edit')}>
												Изменить
											</Button>
											<Button appearance="subtle" icon={<DeleteIcon />} onClick={() => openConfirm()}>
												Удалить
											</Button>
											{node.authorId !== userId && (
												<Button
													appearance="subtle"
													icon={<CrossIcon />}
													onClick={() => setBlocked(node.authorId, !authorBlocked)}
												>
													{authorBlocked ? 'Разрешить автору комментировать' : 'Заблокировать автора'}
												</Button>
											)}
										</div>
									}
								/>
							)}
						</div>
					)}

					{mode === 'reply' && (
						<div className={css.replyForm}>
							<CommentForm
								initialText={`${node.authorName}, `}
								submitLabel="Ответить"
								autoFocus
								onSubmit={handleReply}
								onCancel={() => setMode('view')}
							/>
						</div>
					)}
				</div>
			</div>

			{hasReplies && <CommentList nodes={node.children} depth={depth + 1} />}
		</li>
	);
};
